import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, SearchableCombobox, cn } from "@nexus/ui";
import type { ComboboxOptionGroup } from "@nexus/ui";
import type { PreflightMarketKind } from "@nexus/contracts";
import type { TFunction } from "i18next";
import { marketMatchesQuery } from "../../lib/market-filter";
import { groupMarketsByKind } from "../../lib/market-order";
import { ManageMarketsDialog } from "../ManageMarketsDialog/ManageMarketsDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import type { PreflightMarketWithCount } from "../../types/preflight.types";

/** Combobox key for the all-markets row; market ids are database ids and never collide. */
const ALL_MARKETS_KEY = "__all-markets__";
const ALL_MARKETS_ROW = { id: ALL_MARKETS_KEY } as const;

/** Chips that fit the hub trigger (`max-w-xs`) beside "+N", the clear button and the chevron. */
const MAX_VISIBLE_CHIPS = 2;

type AllMarketsRow = typeof ALL_MARKETS_ROW;
type PickerRow = AllMarketsRow | PreflightMarketWithCount;

/** `null` is the exclusive all-markets scope, which is the hub default (R7). */
export type MarketScope = string[] | null;

type SharedMarketSelectProps = {
  markets: PreflightMarketWithCount[];
  disabled?: boolean;
  /** Trigger height. The hub header pairs it with a primary CTA, so it uses `default`. */
  size?: "sm" | "default";
  /** Extra trigger classes — the create form stretches it across its grid column. */
  className?: string;
};

type ScopeMarketSelectProps = SharedMarketSelectProps & {
  mode?: "scope";
  value: MarketScope;
  onChange: (scope: MarketScope) => void;
  /** Unique live reports — never the sum of per-market membership counts. */
  allMarketsReportCount: number;
};

/**
 * `scope` offers exclusive all-markets plus multi-select markets (hub default,
 * R7). `single` drops all-markets. `multi` is Score create and Change Market —
 * submit stays disabled until at least one id is selected.
 */
export type MarketSelectProps =
  | ScopeMarketSelectProps
  | (SharedMarketSelectProps & {
      mode: "single";
      value: string | null;
      onChange: (id: string | null) => void;
    })
  | (SharedMarketSelectProps & {
      mode: "multi";
      value: string[];
      onChange: (ids: string[]) => void;
    });

/**
 * Market selector for the Pre Flight hub and the create form: a searchable list of
 * every market with its report count, all-markets in scope mode, and a footer action
 * onto the manage panel in every mode (R7, R12, R14). Selection state stays with the
 * caller so the whole hub reads from one source (R8).
 */
export function MarketSelect(props: MarketSelectProps) {
  const { markets, disabled = false, size = "sm", className } = props;
  const { t } = useTranslation();
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isMulti = props.mode === "multi";
  const isSingleSelect = props.mode === "single";
  const isScope = !isMulti && !isSingleSelect;
  const selectedIds = isMulti
    ? props.value
    : isScope
      ? (props.value ?? [])
      : props.value
        ? [props.value]
        : [];
  const selectedMarkets = selectedIds.flatMap((id) => {
    const market = markets.find((row) => row.id === id);
    return market ? [market] : [];
  });
  const firstSelected = selectedMarkets[0];
  const hasMarkets = markets.length > 0;

  const allMarketsLabel = t("preflight.markets.allMarkets", "All markets");
  const noneSelectedLabel = t("preflight.markets.noneSelected", "none selected");
  const multiPlaceholder = t(
    "preflight.markets.selectMarketsPlaceholder",
    "Select markets",
  );
  const singlePlaceholder = t("preflight.markets.selectPlaceholder", "Select a market");

  const selectedNames = selectedMarkets.map((market) => market.name).join(", ");
  const scopeLabel = isMulti
    ? selectedMarkets.length > 0
      ? selectedNames
      : multiPlaceholder
    : isScope && selectedMarkets.length > 0
      ? selectedNames
      : firstSelected?.name ??
        (isSingleSelect ? singlePlaceholder : allMarketsLabel);

  const triggerAria = isMulti
    ? t("preflight.markets.selectMarketsAria", "Markets: {{markets}}", {
        markets:
          selectedMarkets.length > 0
            ? selectedMarkets.map((market) => market.name).join(", ")
            : noneSelectedLabel,
      })
    : isSingleSelect
      ? t("preflight.markets.selectAria", "Market: {{market}}", {
          market: firstSelected?.name ?? noneSelectedLabel,
        })
      : t("preflight.markets.scopeAria", "Market scope: {{scope}}", {
          scope: scopeLabel,
        });

  const isAllMarkets = isScope && (props.value == null || props.value.length === 0);
  const allMarketsReportCount = isScope ? props.allMarketsReportCount : 0;

  const isClearable = !isSingleSelect && selectedMarkets.length > 0;
  const pinnedIdSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const groups = useMemo<ComboboxOptionGroup<PickerRow>[]>(() => {
    const pinnedMarkets = markets
      .filter((market) => pinnedIdSet.has(market.id))
      .sort((a, b) => pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id));
    const selectedGroup =
      pinnedMarkets.length > 0
        ? [{ heading: t("preflight.markets.groupSelected", "Selected"), items: pinnedMarkets }]
        : [];
    const marketGroups = groupMarketsByKind(
      markets.filter((market) => !pinnedIdSet.has(market.id)),
    ).map((group) => ({
      heading: getKindHeading(t, group.kind),
      items: group.markets,
    }));
    // While searching, matches lead so the default highlight (and Enter) picks a
    // match rather than unticking a selected market kept in the list below.
    const isSearching = searchQuery.trim() !== "";
    const listGroups = isSearching
      ? [...marketGroups, ...selectedGroup]
      : [...selectedGroup, ...marketGroups];
    // With no markets, all-markets is the only possible scope; leave it out so
    // the empty state shows instead.
    return isScope && hasMarkets ? [{ items: [ALL_MARKETS_ROW] }, ...listGroups] : listGroups;
  }, [markets, pinnedIds, pinnedIdSet, searchQuery, isScope, hasMarkets, t]);

  const isSearchMatch = useCallback(
    (row: PickerRow, search: string) =>
      // Selected markets stay listed while searching so they can be unticked in place.
      pinnedIdSet.has(row.id) ||
      marketMatchesQuery(
        isAllMarketsRow(row) ? { name: allMarketsLabel, code: "" } : row,
        search,
      ),
    [allMarketsLabel, pinnedIdSet],
  );

  function handlePickerOpenChange(isOpen: boolean) {
    if (isOpen && disabled) return;
    // Pin the selection once per open, not per toggle, so rows never jump under the pointer.
    if (isOpen) setPinnedIds(isSingleSelect ? [] : selectedIds);
    setSearchQuery("");
    setIsPickerOpen(isOpen);
  }

  function handleChipRemove(marketId: string) {
    handleMarketRemoved(marketId);
    triggerRef.current?.focus();
  }

  function handleClear() {
    if (isMulti) props.onChange([]);
    else if (isScope) props.onChange(null);
    triggerRef.current?.focus();
  }

  function handleRowSelect(row: PickerRow) {
    if (isAllMarketsRow(row)) {
      handleAllMarkets();
      setIsPickerOpen(false);
      return;
    }
    if (isMulti) handleMultiToggle(row.id);
    else if (isScope) handleScopeToggle(row.id);
    else handleSingleSelect(row.id);
  }

  function handleManageOpen() {
    setIsPickerOpen(false);
    setIsManageOpen(true);
  }

  function handleMarketRemoved(marketId: string) {
    if (isMulti) {
      props.onChange(props.value.filter((id) => id !== marketId));
      return;
    }
    if (isScope) {
      const current = props.value ?? [];
      if (!current.includes(marketId)) return;
      const next = current.filter((id) => id !== marketId);
      props.onChange(next.length === 0 ? null : next);
      return;
    }
    if (marketId === props.value) props.onChange(null);
  }

  function handleSingleSelect(marketId: string) {
    if (isMulti || isScope) return;
    props.onChange(marketId);
  }

  function handleAllMarkets() {
    if (!isScope) return;
    props.onChange(null);
  }

  function handleScopeToggle(marketId: string) {
    if (!isScope) return;
    const current = props.value ?? [];
    const next = current.includes(marketId)
      ? current.filter((id) => id !== marketId)
      : [...current, marketId];
    props.onChange(next.length === 0 ? null : next);
  }

  function handleMultiToggle(marketId: string) {
    if (!isMulti) return;
    props.onChange(
      props.value.includes(marketId)
        ? props.value.filter((id) => id !== marketId)
        : [...props.value, marketId],
    );
  }

  return (
    <>
      <SearchableCombobox<PickerRow>
        groups={groups}
        getKey={(row) => row.id}
        getLabel={(row) => (isAllMarketsRow(row) ? allMarketsLabel : row.name)}
        isSearchMatch={isSearchMatch}
        onSearchChange={setSearchQuery}
        value={isSingleSelect ? props.value : undefined}
        values={isSingleSelect ? undefined : isAllMarkets ? [ALL_MARKETS_KEY] : selectedIds}
        onSelect={handleRowSelect}
        open={isPickerOpen}
        onOpenChange={handlePickerOpenChange}
        searchPlaceholder={t("preflight.markets.searchPlaceholder", "Search markets")}
        emptyMessage={
          hasMarkets
            ? t("preflight.markets.searchEmpty", "No markets match your search.")
            : t("preflight.markets.selectEmpty", "No markets yet — add one below.")
        }
        contentClassName="min-w-64"
        renderItem={(row, { isSelected }) =>
          isAllMarketsRow(row) ? (
            <PickerRowContent
              icon={<ScopeDot selected={isSelected} square />}
              label={allMarketsLabel}
              reportCount={allMarketsReportCount}
              className="font-medium"
            />
          ) : (
            <PickerRowContent
              icon={<MarketIcon kind={row.kind} code={row.code} name={row.name} />}
              label={row.name}
              reportCount={row.reportCount}
            />
          )
        }
        renderTrigger={({ open }) => (
          <MarketTrigger
            buttonRef={triggerRef}
            open={open}
            ariaLabel={triggerAria}
            title={isClearable ? selectedNames : undefined}
            disabled={disabled}
            size={size}
            className={className}
            chips={isClearable ? selectedMarkets : []}
            onRemoveChip={handleChipRemove}
            onClear={isClearable ? handleClear : undefined}
          >
            {firstSelected ? (
              <MarketIcon
                kind={firstSelected.kind}
                code={firstSelected.code}
                name={firstSelected.name}
              />
            ) : isScope ? (
              <ScopeDot selected square={isAllMarkets} />
            ) : null}
            <span
              className={cn(
                "min-w-0 truncate",
                (isSingleSelect || isMulti) &&
                  selectedMarkets.length === 0 &&
                  "text-muted-foreground",
              )}
            >
              {scopeLabel}
            </span>
          </MarketTrigger>
        )}
        footer={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="plus"
            onClick={handleManageOpen}
            className="text-primary w-full justify-start rounded-none font-medium"
          >
            {t("preflight.markets.addOrManage", "Add or manage markets")}
          </Button>
        }
      />

      {isManageOpen ? (
        <ManageMarketsDialog
          open={isManageOpen}
          onOpenChange={setIsManageOpen}
          markets={markets}
          onMarketRemoved={handleMarketRemoved}
        />
      ) : null}
    </>
  );
}

/**
 * Field-style trigger. The combobox button covers the whole field so any click opens
 * the picker; chip and clear buttons sit above it as siblings, since buttons cannot
 * nest, and stop their clicks before they reach the popover trigger.
 */
function MarketTrigger({
  buttonRef,
  open,
  ariaLabel,
  title,
  disabled,
  size,
  className,
  chips,
  onRemoveChip,
  onClear,
  children,
}: {
  buttonRef: Ref<HTMLButtonElement>;
  open: boolean;
  ariaLabel: string;
  /** Full selection on hover — chips truncate, and their text cannot take pointer events. */
  title?: string;
  disabled: boolean;
  size: "sm" | "default";
  className?: string;
  chips: PreflightMarketWithCount[];
  onRemoveChip: (marketId: string) => void;
  onClear?: () => void;
  /** Label shown when there are no chips: all markets, a placeholder, or the single pick. */
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const hiddenChipCount = chips.length - MAX_VISIBLE_CHIPS;

  return (
    <div
      className={cn(
        "border-input-border bg-background text-foreground hover:bg-muted relative inline-flex min-w-48 max-w-xs items-center gap-1 overflow-hidden rounded-sm border pr-2.5 text-xs transition-colors",
        size === "sm" ? "h-8" : "h-9",
        chips.length > 0 ? "pl-1" : "pl-3",
        disabled && "cursor-not-allowed opacity-40",
        className,
      )}
    >
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        disabled={disabled}
        className="focus-visible:ring-ring/40 absolute inset-0 cursor-pointer rounded-sm outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed"
      />
      <span className="pointer-events-none relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {chips.length > 0 ? (
          <>
            {chips.slice(0, MAX_VISIBLE_CHIPS).map((market) => (
              <MarketChip
                key={market.id}
                market={market}
                onRemove={disabled ? undefined : () => onRemoveChip(market.id)}
              />
            ))}
            {hiddenChipCount > 0 ? (
              <span className="bg-primary-subtle text-primary-subtle-foreground shrink-0 rounded-sm px-1.5 py-0.5 font-medium">
                {t("preflight.markets.moreSelected", "+{{count}}", { count: hiddenChipCount })}
              </span>
            ) : null}
          </>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">{children}</span>
        )}
      </span>
      {onClear && !disabled ? (
        <IconButton
          label={t("preflight.markets.clearSelection", "Clear selected markets")}
          onClick={onClear}
        />
      ) : null}
      <Icon
        name="chevron-down"
        size="sm"
        color="inherit"
        aria-hidden
        className="pointer-events-none relative shrink-0"
      />
    </div>
  );
}

function MarketChip({
  market,
  onRemove,
}: {
  market: PreflightMarketWithCount;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span className="border-border bg-muted inline-flex h-6 min-w-0 max-w-28 items-center gap-1 rounded-sm border pr-0.5 pl-1.5">
      <MarketIcon kind={market.kind} code={market.code} name={market.name} />
      <span className="min-w-0 truncate">{market.name}</span>
      {onRemove ? (
        <IconButton
          label={t("preflight.markets.deselectMarket", "Remove {{name}} from selection", {
            name: market.name,
          })}
          onClick={onRemove}
        />
      ) : null}
    </span>
  );
}

/** ✕ button above the trigger's combobox button; it must not also toggle the picker. */
function IconButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="text-muted-foreground hover:bg-background hover:text-foreground focus-visible:ring-ring/40 pointer-events-auto relative flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-xs outline-none focus-visible:ring-2"
    >
      <Icon name="x" size="xs" color="inherit" />
    </button>
  );
}

function isAllMarketsRow(row: PickerRow): row is AllMarketsRow {
  return row.id === ALL_MARKETS_KEY;
}

/** Global is a single row — no section heading (same as the manage catalogue). */
function getKindHeading(t: TFunction, kind: PreflightMarketKind): string | undefined {
  if (kind === "GLOBAL") return undefined;
  return kind === "CONTINENT"
    ? t("preflight.markets.groupContinents", "Continents")
    : t("preflight.markets.groupCountries", "Countries");
}

function PickerRowContent({
  icon,
  label,
  reportCount,
  className,
}: {
  icon: ReactNode;
  label: string;
  reportCount: number;
  className?: string;
}) {
  return (
    <span className={cn("flex min-w-0 flex-1 items-center gap-2.5", className)}>
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <ReportCount value={reportCount} />
    </span>
  );
}

/** Selection marker; the square shape distinguishes the all-markets roll-up from a market. */
function ScopeDot({
  selected,
  square = false,
}: {
  selected: boolean;
  square?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2 shrink-0",
        square ? "rounded-xs" : "rounded-full",
        selected ? "bg-primary" : "bg-muted-foreground/35",
      )}
    />
  );
}

function ReportCount({ value }: { value: number }) {
  const { t } = useTranslation();
  return (
    <span
      className="text-muted-foreground shrink-0 text-xs tabular-nums"
      aria-label={t("preflight.markets.reportCount", {
        count: value,
        defaultValue_one: "{{count}} report",
        defaultValue_other: "{{count}} reports",
      })}
    >
      {value}
    </span>
  );
}
